import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import {
  ApiRequestParams,
  EventTaskSpawnRequest,
  TaskboardInfo,
} from 'src/models/global-interfaces';
import { createHttpParams } from 'src/services/utilities';
import { format } from 'date-fns';
import { DialogService } from './dialog.service';
import { TaskView } from 'src/components/task/task-view';
import {
  ScheduleEnabledSettingsDialog,
  ScheduleEnabledSettingsPayload,
} from 'src/dialogs/schedule-enabled-settings/schedule-enabled-settings.dialog';
import { DefaultColorValue, TaskScope } from 'src/models/global-enum';
import { TaskItem } from 'src/models/task-item.model';
import {
  taskTableApiRequestParams,
  TaskPreview,
  AssignableTaskRequest,
  AssignableTaskResource,
  EditTask,
  WorkOnTask,
  TaskInfo,
  TaskSettings,
  CreateTaskRequest,
  UpdateTaskRequest,
  BaseTask,
  TaskUpdateTags,
  DeployTaskRequest,
  TaskReoccurs,
  RecallDeployedTaskRequest,
  TaskReoccursRequest,
  UnlinkTaskPayload,
  TaskSetTask,
  SpawnTaskRequest,
  IncompleteTask,
  EventTaskInfoResponse,
  DelayTaskRequest,
  ClosedTaskRequest,
  ClosedTaskResponse,
  SendTaskReminderRequest,
  CreateScorecardRequest,
  ScorecardResource,
  UpdateScorecardRequest,
  ReassignTaskRequest,
  CopyTaskRequest,
  UpdateTaskNameAdditionRequest,
  FailTaskRequest,
} from 'src/models/task.model';

export interface TaskType {
  typeName: string;
  typeId: 1 | 2;
}

export interface SpawnTaskResponse {
  id: number;
  spawnMultiple: boolean;
}

export interface TaskScore {
  score: number;
}

@Injectable()
export class TaskService {
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);
  private apiUrl = environment.apiUrl;

  getTasks = (params: ApiRequestParams = taskTableApiRequestParams) =>
    this.http.get<TaskPreview[]>(`${this.apiUrl}tasks`, {
      params: createHttpParams(params),
    });

  getAssignableTasks = (params: AssignableTaskRequest = taskTableApiRequestParams) =>
    this.http.get<AssignableTaskResource[]>(`${this.apiUrl}tasks/assignable-tasks`, {
      params: createHttpParams(params),
    });

  getTaskById = (taskId: number) => this.http.get<BaseTask>(`${this.apiUrl}tasks/${taskId}`);

  getTaskForEdit = (taskID: number) =>
    this.http.get<EditTask>(`${this.apiUrl}tasks/${taskID}/editTask`);

  getRunningTaskScore = (taskId: number) =>
    this.http.get<TaskScore>(`${this.apiUrl}tasks/${taskId}/taskScore`);

  getTaskForWork = (taskID: number) =>
    this.http.get<WorkOnTask>(`${this.apiUrl}tasks/${taskID}/workOnTask`);

  getTaskInfo = (taskID: number) =>
    this.http.get<TaskInfo>(`${this.apiUrl}tasks/${taskID}/taskInfo`);

  getTaskSettings = (taskID: number) =>
    this.http.get<TaskSettings>(`${this.apiUrl}tasks/${taskID}/taskSettings`);

  createTask = (req?: CreateTaskRequest) =>
    this.http.post<TaskPreview>(`${this.apiUrl}tasks`, {
      ...req,
      name: 'New Task Template',
    });

  updateTask = (taskId: number, task: UpdateTaskRequest) =>
    this.http.put<BaseTask>(`${this.apiUrl}tasks/${taskId}`, task);

  failTask = (taskId: number, req: FailTaskRequest) =>
    this.http.put<BaseTask>(`${this.apiUrl}tasks/${taskId}/failTask`, req);

  updateNameAddition = (taskId: number, req: UpdateTaskNameAdditionRequest) =>
    this.http.patch<unknown>(`${this.apiUrl}tasks/updateNameAddition/${taskId}`, req);

  reassignTask = (task?: ReassignTaskRequest) =>
    this.http.post<BaseTask>(`${this.apiUrl}reassign-task`, task);

  updateTaskTags = (taskId: number, req?: TaskUpdateTags) =>
    this.http.put<unknown>(`${this.apiUrl}tasks/${taskId}`, req);

  deleteTask = (taskID: number, reason: string) =>
    this.http.delete<unknown>(`${this.apiUrl}tasks/${taskID}`, { body: { reason } });

  closeOpenTaskItems = (taskID: number, locationID: number, failTaskOption: number) =>
    this.http.post<unknown>(`${this.apiUrl}close-open-task-item-tasks`, {
      taskID,
      locationID,
      failTaskOption,
    });

  taskDeployment = (payload: DeployTaskRequest) => {
    return this.http.post<unknown>(`${this.apiUrl}deploy-task`, payload);
  };

  duplicateTask = (payload: CopyTaskRequest) =>
    this.http.post<unknown>(`${this.apiUrl}tasks/copy`, payload);

  duplicateSchedule = (taskReoccurID: number) =>
    this.http.patch<TaskReoccurs>(`${this.apiUrl}task-reoccur/duplicate/${taskReoccurID}`, {});

  resetTask = (taskID: number) =>
    this.http.patch<unknown>(`${this.apiUrl}tasks/reset/${taskID}`, {});

  recallTaskDeployment = (payload: RecallDeployedTaskRequest) =>
    this.http.post<unknown>(`${this.apiUrl}recall-deployed-task`, payload);

  unlinkTaskDocument = (documentID: number) =>
    this.http.delete<unknown>(`${this.apiUrl}task-document/${documentID}`);

  updateTaskReoccursWithPastCheck = (
    reocurrence: TaskReoccurs,
    isTaskSet: boolean,
    isAssignedRole: boolean,
    profileName: string,
  ) => {
    // Returns a boolean observable that returns the final reoccurs value
    return new Observable<TaskReoccurs>((observer: Subscriber<TaskReoccurs>) => {
      const startAtDate: Date = new Date(reocurrence.startAt!);
      const today: Date = new Date();
      let isBackDated = false;

      // Basically we need to know if the start date is in the past
      // If it is in the past, make sure it's not the same day
      if (reocurrence.is_active && startAtDate < today) {
        if (startAtDate.getFullYear() !== today.getFullYear()) {
          isBackDated = true;
        } else if (startAtDate.getMonth() !== today.getMonth()) {
          isBackDated = true;
        } else if (startAtDate.getDate() !== today.getDate()) {
          isBackDated = true;
        }
      }

      if ((isBackDated || isAssignedRole) && reocurrence.is_active) {
        reocurrence.spawnMultiple = false;
        reocurrence.spawnPrevious = false;
        this.dialogService.open<ScheduleEnabledSettingsDialog, ScheduleEnabledSettingsPayload>({
          component: ScheduleEnabledSettingsDialog,
          payload: {
            reocurrence,
            isTaskSet,
            isAssignedRole,
            isBackDated,
            profileName,
          },
          disableClose: false,
          width: '600px',
          onConfirm: (updatedReocurrence: TaskReoccurs) => {
            this.updateTaskReoccurs(updatedReocurrence).subscribe(() => {
              observer.next(updatedReocurrence);
              observer.complete();
            });
            return true;
          },
          onCancel: () => {
            observer.next(reocurrence);
            observer.complete();
          },
        });
      } else {
        this.updateTaskReoccurs(reocurrence).subscribe(() => {
          observer.next(reocurrence);
          observer.complete();
        });
      }
    });
  };

  updateTaskReoccurs = (taskReoccurs: TaskReoccurs) => {
    const taskReoccursRequest: TaskReoccursRequest = {
      ...taskReoccurs,
      ...(taskReoccurs.startAt && {
        startAt: format(new Date(taskReoccurs.startAt!), 'yyyy-MM-dd HH:mm:ss'),
      }),
    };

    return this.http.put<unknown>(
      `${this.apiUrl}task-reoccur/${taskReoccursRequest.id}`,
      taskReoccursRequest,
    );
  };

  unlinkTask = (task: UnlinkTaskPayload) =>
    this.http.put<unknown>(`${this.apiUrl}unlink-task-from-task-set/${task.taskID}`, task);

  updateTaskOrder = (tasks?: TaskSetTask[]) =>
    this.http.put<unknown>(`${this.apiUrl}tasks/task-set-order`, { tasks }).pipe();

  spawnTask = (task?: SpawnTaskRequest) =>
    this.http.post<SpawnTaskResponse>(`${this.apiUrl}tasks/spawn-task`, task);

  spawnEventTask = (task?: SpawnTaskRequest) =>
    this.http.post<unknown>(`${this.apiUrl}tasks/spawn-event-task`, task);

  getIncompleteTasks = (params: ApiRequestParams, locationId: number) =>
    this.http.get<IncompleteTask[]>(`${this.apiUrl}incomplete-tasks/${locationId}`, {
      params: createHttpParams(params),
    });

  getEventTasks = (params: ApiRequestParams) =>
    this.http.post<unknown>(`${this.apiUrl}event-info`, params);

  getEventTaskSpawnInfo = (params: EventTaskSpawnRequest) =>
    this.http.post<EventTaskInfoResponse>(`${this.apiUrl}tasks/event-task-spawn-info`, params);

  getTaskboardInfo = (req: ApiRequestParams) =>
    this.http.get<TaskboardInfo>(`${this.apiUrl}task-board/?locationID=${req.locationID}`);

  delayTask = (req?: DelayTaskRequest) => this.http.post<unknown>(`${this.apiUrl}task-delays`, req);

  getClosedTasks = (req: ClosedTaskRequest) => {
    let url = `${this.apiUrl}closed-tasks?locationID=${req.locationID}`;
    if (req.completedStartDate) url += `&completedStartDate=${req.completedStartDate}`;
    if (req.completedEndDate) url += `&completedEndDate=${req.completedEndDate}`;
    if (req.dueStartDate) url += `&dueStartDate=${req.dueStartDate}`;
    if (req.dueEndDate) url += `&dueEndDate=${req.dueEndDate}`;
    if (req.taskType) url += `&taskType=${req.taskType}`;
    if (req.scoredTask) url += `&scoredTask=${req.scoredTask}`;
    return this.http.get<ClosedTaskResponse[]>(url);
  };

  sendReminder(req?: SendTaskReminderRequest) {
    return this.http.post(`${this.apiUrl}tasks/send-reminder`, req, { observe: 'response' });
  }

  completeTask = (id: number) => this.http.post<unknown>(`${this.apiUrl}complete-task`, { id });

  getTaskScore(taskId: number): Observable<ScorecardResource> {
    return this.http.get<ScorecardResource>(`${this.apiUrl}tasks/${taskId}/scorecard`);
  }

  setTaskScore = (
    taskId: number,
    score: number,
    task_observation: string,
    task_recommendation: string,
  ) => {
    const params: CreateScorecardRequest = {
      task_id: taskId,
      score: score,
      task_observation: task_observation,
      task_recommendation: task_recommendation,
    };
    return this.http.post<ScorecardResource>(`${this.apiUrl}score-task`, params);
  };

  updateTaskScore(
    score: number,
    task_observation: string,
    task_recommendation: string,
    scoreId: number,
  ): Observable<ScorecardResource> {
    const params: UpdateScorecardRequest = {
      score: score,
      task_observation: task_observation,
      task_recommendation: task_recommendation,
      id: scoreId,
    };
    return this.http.patch<ScorecardResource>(`${this.apiUrl}score-task/${scoreId}`, params);
  }

  deleteTaskItemFromArray = (taskItems: TaskItem[], itemId: number) => {
    let indexToDelete = -1;
    taskItems.forEach((item, index: number) => {
      if (indexToDelete === -1) {
        if (item.id === itemId) {
          indexToDelete = index;
        } else if (item.children) {
          this.deleteTaskItemFromArray(item.children, itemId);
        }
      }
    });

    if (indexToDelete !== -1) {
      taskItems.splice(indexToDelete, 1);
    }
  };

  calcDaysPastDue = (
    date: string | null,
    color: number,
  ): { color: string | null; status: string | null } => {
    const output: { color: string | null; status: string | null } = {
      color: DefaultColorValue[color] || 'none',
      status: null as string | null,
    };
    if (date) {
      const dueDate: Date = new Date(date);
      const today: Date = new Date();
      const diffTime: number = dueDate.getTime() - today.getTime();
      const diffDays: number = Math.round(diffTime / (1000 * 60 * 60 * 24));
      const diffHours: number = Math.round(diffTime / (1000 * 60 * 60));
      const diffMinutes: number = diffTime / (1000 * 60);
      const unit: string = Math.abs(diffDays) > 1 ? 'Days' : 'Day';

      // Calculate due time to nearest day
      if (Math.abs(diffHours) < 24) {
        output.status = this.calcHoursPastDue(diffHours, diffMinutes);
        return output;
      }

      if (diffDays < 0) {
        output.status = `${Math.ceil(Math.abs(diffDays))} ${unit} Overdue`;
      }
      if (diffDays > 0) {
        output.status = `${Math.ceil(diffDays)} ${unit} Left`;
      }
    }

    return output;
  };

  getDaysPastDueHtml = (
    dueDateStatus: { color: string | null; status: string | null },
    isDelayed: boolean,
  ) => {
    if (isDelayed) {
      return `
        <div class="due-display">
          <span class="due-status ${dueDateStatus.color}">${dueDateStatus.status} (delayed)</span>
        </div>
      `;
    } else {
      return `
        <div class="due-display">
          <span class="due-status ${dueDateStatus.color}">${dueDateStatus.status}</span>
        </div>
      `;
    }
  };

  private calcHoursPastDue = (diffHours: number, diffMinutes: number) => {
    let output = '';
    const unit: string = Math.abs(diffHours) > 1 ? 'Hours' : 'Hour';

    // Calculate due time to nearest hour
    if (Math.abs(diffHours) < 1) {
      output = this.calcMinutesPastDue(diffMinutes);
      return output;
    }
    if (diffHours < 0) output = `${Math.round(Math.abs(diffHours))} ${unit} Overdue`;
    if (diffHours > 0) output = `${Math.round(diffHours)} ${unit} Left`;

    return output;
  };

  private calcMinutesPastDue = (diffMinutes: number) => {
    let output = '';
    const unit: string = Math.abs(diffMinutes) > 2 ? 'Minutes' : 'Minute';

    // Calculate due time to nearest minute (rounding down)
    if (Math.abs(Math.floor(diffMinutes)) === 0) {
      output = 'Due Now';
      return output;
    }
    if (Math.floor(diffMinutes) < 1)
      output = `${Math.floor(Math.abs(diffMinutes))} ${unit} Overdue`;
    if (Math.floor(diffMinutes) >= 1) output = `${Math.floor(diffMinutes)} ${unit} Left`;
    return output;
  };

  static getViewFromScope(scope: TaskScope): TaskView {
    switch (scope) {
      case TaskScope.LOCAL:
        return TaskView.LOCAL;
      case TaskScope.GLOBAL:
        return TaskView.GLOBAL;
      case TaskScope.DEPLOYED:
        return TaskView.DEPLOYED;
      case TaskScope.EVENT:
        return TaskView.EVENT;
      case TaskScope.COMPLETED:
        return TaskView.COMPLETED;
      case TaskScope.FAILED:
        return TaskView.FAILED;
      case TaskScope.SPAWNED:
        return TaskView.SPAWNED;
      default:
        return TaskView.LOCAL;
    }
  }
}
